import React, { useState } from "react";
import { Button, Input, Textarea } from "@lemonadetraining/web-ui";
import { useDispatch, useSelector } from "react-redux";
import { httpsCallable } from "firebase/functions";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import { firebaseFunctions } from "@lemonadetraining/web-shared";
import { callableMessage } from "../../lib/callableError";

// score, kg, time and timeString have no inputs since 2024-01 but are still sent, unchanged.
const SCORE_INIT = {
  score: 0,
  rpe: 0,
  kg: 0.0,
  time: 0,
  timeString: "0:00",
  comments: "",
};

// The input yields a string; submitResults accepts only a number in 0-10, decimals allowed.
const toRpe = (value) => {
  const rpe = Number(value);
  return Number.isFinite(rpe) ? Math.min(10, Math.max(0, rpe)) : 0;
};

const WorkoutScore = (props) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const [submiting, setSubmitting] = useState(false);
  const [diableSubmit, setDisableSubmit] = useState(false);
  const [score, setScore] = useState(props.score || SCORE_INIT);
  const [saveError, setSaveError] = useState(null);

  /* --------[handlers] ------------- */
  const onChangeHandler = (event) => {
    const { name, value } = event.target;

    setScore((prev) => ({ ...prev, [name]: value }));
  };

  // `saved` tells the caller whether a result was recorded (true) or failed (false).
  const handleClose = (saved) => {
    if (props.onSubmitFinished) {
      props.onSubmitFinished(saved);
    }
  };

  /* ------------------------ */
  const onSubmitResultHandler = async () => {
    if (!user.uid) {
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        title: "user UID",
        message: "User UID not set correctly",
      };
      dispatch(notifyActions.sendNotification(notification));
      return;
    }

    setSubmitting(true);
    setSaveError(null);
    const scoreData = {
      workoutUID: props.workoutUID,
      athleteUID: user.uid,
      athleteName: user.displayName,
      ...score,
      rpe: toRpe(score.rpe),
    };

    const submitWorkoutResults = httpsCallable(firebaseFunctions, "submitWorkoutResults");

    await submitWorkoutResults(scoreData)
      .then(() => {
        const notification = {
          severity: NOTIFICATION_TYPE.success,
          title: "Result submitted",
          message: "Your workout result have been successfully submitted",
        };
        dispatch(notifyActions.sendNotification(notification));
        setSubmitting(false);
        setDisableSubmit(true);
        handleClose(true);
      })
      .catch((err) => {
        setSaveError(
          callableMessage(err) ||
            "We couldn't save your result. Your entry is still here, so you can try again."
        );
        setSubmitting(false);
        handleClose(false);
      });
  };

  return (
    <div className="flex flex-col gap-2 p-1">
      <div className="flex flex-row">
        <div className="flex-auto" />
        <div className="flex flex-col">
          <label className="text-sm font-medium">RPE</label>
          <Input
            name="rpe"
            type="number"
            value={score.rpe}
            onChange={onChangeHandler}
            min={0}
            max={10}
            className="w-[150px]"
            disabled={props.viewOnly}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-sm font-medium">Comments</label>
        <Textarea
          name="comments"
          className="w-[400px]"
          rows={2}
          value={score.comments}
          onChange={onChangeHandler}
          disabled={!props.workoutUID}
        />
      </div>
      {saveError && (
        <p role="alert" className="text-sm text-destructive">
          {saveError}
        </p>
      )}
      {!props.viewOnly && (
        <Button onClick={onSubmitResultHandler} disabled={submiting || diableSubmit}>
          {submiting ? "Submitting..." : diableSubmit ? "Submitted" : "Submit Result"}
        </Button>
      )}
    </div>
  );
};

export default WorkoutScore;
